"use client";

import { useParams, usePathname, useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";
import { useDataLayerUser } from "@/components/datalayer-identity";
import { getServerConfig } from "@/lib/server/actions/server-config";
import {
  initializeGTM,
  pageviewToDataLayer,
  virtualPageviewToDataLayer,
} from "@/utils/gtm.utils";

interface PageAnalyticsData {
  division?: string;
  /** Reinitialize page metadata on client navigation between pages sharing a layout. */
  trackPageChanges?: boolean;
  trackVirtualPageView?: boolean;
  type: string;
}

export function PageAnalytics({
  division,
  trackPageChanges = false,
  trackVirtualPageView = false,
  type,
}: PageAnalyticsData) {
  const navParams = useParams();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { brand } = getServerConfig();
  // Server-resolved identity, available synchronously so dataLayer_ready carries
  // the user without an async lookup (and so it fires before virtual_page_view).
  const user = useDataLayerUser();
  const initializedRef = useRef(false);
  const initializedPageRef = useRef("");
  const trackedPathRef = useRef("");

  const country = navParams.country ?? "";
  const lang = navParams.lang ?? "";
  const currentBrand =
    brand.value.charAt(0).toUpperCase() + brand.value.slice(1);
  const currentSearchParams = searchParams.toString();
  const pageUrl = currentSearchParams
    ? `${pathname}?${currentSearchParams}`
    : pathname;

  useEffect(() => {
    initializeGTM();

    // A product-to-product client navigation can reuse this component. Query
    // changes (including article selection) do not represent a new page.
    const pageKey = `${pathname}|${country}|${lang}|${currentBrand}|${type}|${division ?? ""}`;
    if (
      !initializedRef.current ||
      (trackPageChanges && initializedPageRef.current !== pageKey)
    ) {
      pageviewToDataLayer(
        country,
        lang,
        currentBrand,
        type,
        division,
        user ?? undefined
      );
      initializedRef.current = true;
      initializedPageRef.current = pageKey;
    }

    if (!trackVirtualPageView) {
      return;
    }

    const trackingKey = `${pageUrl}|${type}|${division ?? ""}`;
    if (trackedPathRef.current === trackingKey) {
      return;
    }

    virtualPageviewToDataLayer({
      pageUrl,
      country,
      lang,
      brand: currentBrand,
      type,
      division,
    });

    trackedPathRef.current = trackingKey;
  }, [
    country,
    lang,
    division,
    type,
    currentBrand,
    pathname,
    pageUrl,
    trackPageChanges,
    trackVirtualPageView,
    user,
  ]);

  return null;
}
